import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import { acceptBillOfSale, getBillOfSaleDocument } from "../lib/apiBillOfSale";

export default function BillOfSaleAcceptModal({
  open,
  artworkId,
  onClose,
  onAccepted,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !artworkId) return;

    let active = true;
    setLoading(true);
    setChecked(false);
    setError("");
    setDocumentData(null);

    (async () => {
      try {
        const data = await getBillOfSaleDocument(artworkId);
        if (!active) return;
        setDocumentData(data);
      } catch (err) {
        if (!active) return;
        setError(err?.payload?.detail || err?.message || "Failed to load Bill of Sale.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, artworkId]);

  const handleAccept = async () => {
    if (!artworkId) return;
    try {
      setSubmitting(true);
      setError("");
      await acceptBillOfSale(artworkId, {
        workflow: "artwork_purchase",
      });
      await onAccepted?.();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Failed to accept Bill of Sale.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={loading || submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "94vw", sm: 680 },
          maxHeight: { xs: "88vh", sm: "84vh" },
          overflow: "hidden",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="h6">Bill of Sale</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            You must accept before continuing to payment.
          </Typography>
        </Box>

        {(loading || submitting) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {loading ? "Loading Bill of Sale..." : "Recording acceptance..."}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ px: 3, pb: 2, overflowY: "auto", flex: 1 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              bgcolor: "#fafafa",
              minHeight: 280,
              whiteSpace: "pre-wrap",
              fontFamily: "Georgia, serif",
              fontSize: 14,
              lineHeight: 1.65,
            }}
          >
            {documentData ? (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {documentData.title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                  Version {documentData.version}
                </Typography>
                <Typography variant="body2">
                  {documentData.content}
                </Typography>
              </>
            ) : (
              !loading && <Typography variant="body2">No Bill of Sale document is available.</Typography>
            )}
          </Box>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={loading || submitting || !documentData}
              />
            }
            label="I have read and accept this Bill of Sale."
          />
        </Box>

        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button onClick={onClose} disabled={loading || submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={!checked || loading || submitting || !documentData}
          >
            Accept and Continue to Payment
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
